import React, { useState, useEffect, useContext, useRef } from 'react'
import { useParams } from 'react-router'
import {
  IPTV_CHANNEL_PLAYLIST,
  GET_ACTIVE_SUBSCRIPTIONS,
  IPTV_CHANNEL_FILTER,
} from '../../../utils/apiUrls'
import useAxios from 'axios-hooks'
import {
  create_share_url,
  getAdUrl,
  getAudioUrl,
  getImage,
  getTrailerUrl,
} from '../../../utils/helper'
import { AppContext } from '../../../context/app'
import { intersectionWith, isEmpty, isEqual } from 'lodash'
import { SubscriptionContext } from '../../../context/subscription'
import moment from 'moment'
import { useInterval } from '../../../hooks/useInterval'
import { useTranslation } from 'react-i18next'
import ChannelPlayer from '../../player/channelPlayer'
import IptvPlaylistEPG from '../iptvPlaylistEPG'
import CustomImage from '../../../components/customImage/customImage'
import LockIcon from '../../../assets/lock-icon.svg'
import { withTheme } from 'theming'
import IptvFavorite from '../../../assets/imgComponents/iptvFavorite'
import IptvShare from '../../../assets/imgComponents/iptvShare'
import CategoryNavigatorCards from '../../../components/template/default/categoryNavigatorCards'
import ShareModal from '../../../components/shareModal/shareModal'

const IptvDefaultPage = ({ theme, data }) => {
  const channelId = useParams().channelId
  const { t } = useTranslation()
  const { isLogin, setOpenLoginModal, setModalType, accountObj } = useContext(AppContext)
  const {
    activeSubscription,
    setOpenSubscriptionModal,
    setSubscriptions,
    setRedirectionUrl,
    setContentPartners,
  } = useContext(SubscriptionContext)

  const [detailObj, setDetailObj] = useState({})
  const [isChannelSubscribed, setIsChannelSubscribed] = useState(false)
  const [currentPlayingContent, setCurrentPlayingContent] = useState({})
  const [videoSource, setVideoSource] = useState(null)
  const [videoURLError, setVideoURLError] = useState('')
  const [iptvLoader, setIptvLoader] = useState(false)
  const [informaticButtonHover, setInformaticButtonHover] = useState('')
  const [shareModal, setShareModal] = useState(false)

  const playlistRef = useRef([])
  const playlistSignatureRef = useRef('')
  const currentProgramRef = useRef(null)
  const blockedProgramRef = useRef({
    startMs: null,
    endMs: null,
  })
  const lastSeenExactEndMsRef = useRef(null)
  const loopStateRef = useRef({
    active: false,
    broken: false,
    sequence: [],
    sequenceSignature: '',
    index: 0,
    startedAtMs: 0,
  })

  const [{ data: channelDetails, loading: channelDetailsLoading }, channelDetailsCall] = useAxios(
    {
      url: IPTV_CHANNEL_FILTER,
      method: 'post',
    },
    {
      manual: true,
    },
  )

  const [{ loading: activeSubLoading }, activeSubCall] = useAxios(
    {
      url: GET_ACTIVE_SUBSCRIPTIONS,
      method: 'get',
    },
    { manual: true },
  )

  const [, playlistPollingCall] = useAxios(
    {
      url: IPTV_CHANNEL_PLAYLIST,
      method: 'get',
    },
    { manual: true },
  )

  useEffect(() => {
    setIptvLoader(channelDetailsLoading || activeSubLoading)
  }, [channelDetailsLoading, activeSubLoading])

  useEffect(() => {
    setIsChannelSubscribed(true)

    setVideoSource(null)
    setVideoURLError('')
    setCurrentPlayingContent({})

    playlistRef.current = []
    playlistSignatureRef.current = ''
    currentProgramRef.current = null
    blockedProgramRef.current = { startMs: null, endMs: null }
    lastSeenExactEndMsRef.current = null

    channelDetailsCall({
      data: {
        filters: { channel_ids: [channelId] },
        page: '1',
      },
    })
  }, [channelId])

  const getPlaylistStreamUrl = (program) => {
    if (program?.is_hls_url && program?.hls_link) {
      return program.hls_link
    }

    if (!accountObj?.cf_domain_name || !program?.asset_id) {
      return ''
    }

    if (program?.is_trailer) {
      return getTrailerUrl(accountObj?.cf_domain_name, program.asset_id, 480)
    }

    if (program?.is_advertisement) {
      return getAdUrl(accountObj?.cf_domain_name, program.asset_id, 480)
    }

    if (program.file_metadata?.file_type.includes('audio')) {
      return getAudioUrl(accountObj?.cf_domain_name, program.asset_id)
    }

    const baseUrl = /^https?:\/\//i.test(accountObj.cf_domain_name)
      ? accountObj.cf_domain_name
      : `https://${accountObj.cf_domain_name}`

    return `${baseUrl.replace(/\/$/, '')}/assets/${program.asset_id}/hls/master.m3u8`
  }

  const normalizePlaylistData = (playlist = []) => {
    return (playlist || [])
      .map((program) => {
        const startMoment = moment.utc(program?.start_time)
        let endMoment = moment.utc(program?.end_time)

        if (!startMoment.isValid() || !endMoment.isValid()) return null
        if (endMoment.isSameOrBefore(startMoment)) {
          endMoment = endMoment.add(1, 'day')
        }

        return {
          ...program,
          title: program?.title || program?.file_name || 'Untitled Program',
          description: program?.description || '',
          playUrl: getPlaylistStreamUrl(program),
          _startMoment: startMoment,
          _endMoment: endMoment,
          _programKey: `${program?.asset_id || program?.file_name}-${program?.start_time}`,
          _loopDurationMs: Math.max(
            1000,
            Math.round(
              ((Number(program?.duration) > 0
                ? Number(program?.duration)
                : Math.max(1, endMoment.diff(startMoment, 'seconds', true))) || 1) * 1000,
            ),
          ),
        }
      })
      .filter(Boolean)
      .sort((first, second) => first._startMoment.valueOf() - second._startMoment.valueOf())
  }

  const getLoopSignature = (sequence = []) => {
    return sequence.map((item) => `${item._programKey}-${item._loopDurationMs}`).join('|')
  }

  const getLatestCompletedExactEndMs = (playlist = [], nowUtc = moment.utc()) => {
    const nowMs = nowUtc.valueOf()
    return playlist.reduce((latestEndMs, item) => {
      if (!item?.has_exact_time) return latestEndMs

      const exactEndMs = item?._endMoment?.valueOf() || 0
      if (exactEndMs > 0 && nowMs >= exactEndMs) {
        return Math.max(latestEndMs, exactEndMs)
      }

      return latestEndMs
    }, 0)
  }

  const getLoopSequence = (
    playlist = [],
    nowUtc = moment.utc(),
    brokenBeforeMs = 0,
    blockedProgramStartMs = null,
  ) => {
    const nowMs = nowUtc.valueOf()

    return playlist.filter((item) => {
      if (!item?.is_looped) return false
      if (blockedProgramStartMs !== null && item?._startMoment?.valueOf() <= blockedProgramStartMs)
        return false

      const loopStartMs = item?._startMoment?.valueOf() || 0
      const loopEndMs = item?._endMoment?.valueOf() || 0

      // Loops that ended before the latest completed exact-time boundary are considered broken.
      if (brokenBeforeMs > 0 && loopEndMs > 0 && loopEndMs <= brokenBeforeMs) return false

      // Only loops that have started should participate in loop playback selection.
      return loopStartMs > 0 && nowMs >= loopStartMs
    })
  }

  const syncLoopState = (loopSequence = [], preferredProgramKey = null) => {
    const state = loopStateRef.current
    const signature = getLoopSignature(loopSequence)

    if (!signature) {
      loopStateRef.current = {
        active: false,
        broken: false,
        sequence: [],
        sequenceSignature: '',
        index: 0,
        startedAtMs: 0,
      }
      return
    }

    if (!state.active) {
      const preferredIndex = loopSequence.findIndex(
        (item) => item._programKey === preferredProgramKey,
      )
      loopStateRef.current = {
        active: true,
        broken: false,
        sequence: loopSequence,
        sequenceSignature: signature,
        index: preferredIndex >= 0 ? preferredIndex : 0,
        startedAtMs: moment.utc().valueOf(),
      }
      return
    }

    if (state.sequenceSignature !== signature) {
      const currentKey = state.sequence[state.index]?._programKey
      const nextIndex = loopSequence.findIndex(
        (item) => item._programKey === currentKey || item._programKey === preferredProgramKey,
      )
      loopStateRef.current = {
        active: true,
        broken: false,
        sequence: loopSequence,
        sequenceSignature: signature,
        index: nextIndex >= 0 ? nextIndex : 0,
        startedAtMs: moment.utc().valueOf(),
      }
    }
  }

  const getActiveLoopProgram = (nowMs) => {
    const state = loopStateRef.current
    if (!state.active || isEmpty(state.sequence)) return null

    let guard = 0
    while (guard < state.sequence.length * 2) {
      const currentProgram = state.sequence[state.index]
      const durationMs = Math.max(1000, currentProgram?._loopDurationMs || 1000)

      if (nowMs - state.startedAtMs < durationMs) {
        return currentProgram
      }

      state.startedAtMs += durationMs
      state.index = (state.index + 1) % state.sequence.length
      guard += 1
    }

    return state.sequence[state.index] || null
  }

  const initializeLoopStateFromTimeline = (
    loopSequence = [],
    nowMs,
    preferredProgramKey = null,
  ) => {
    if (isEmpty(loopSequence)) return

    if (preferredProgramKey) {
      syncLoopState(loopSequence, preferredProgramKey)
      return
    }

    const totalDurationMs = loopSequence.reduce(
      (sum, item) => sum + Math.max(1000, item?._loopDurationMs || 1000),
      0,
    )
    if (totalDurationMs <= 0) {
      syncLoopState(loopSequence)
      return
    }

    const firstStartMs = loopSequence[0]?._startMoment?.valueOf() || nowMs
    const elapsedSinceStart = Math.max(0, nowMs - firstStartMs)
    let offsetInCycle = elapsedSinceStart % totalDurationMs

    let index = 0
    while (index < loopSequence.length) {
      const durationMs = Math.max(1000, loopSequence[index]?._loopDurationMs || 1000)
      if (offsetInCycle < durationMs) break
      offsetInCycle -= durationMs
      index += 1
    }

    if (index >= loopSequence.length) {
      index = 0
      offsetInCycle = 0
    }

    loopStateRef.current = {
      active: true,
      broken: false,
      sequence: loopSequence,
      sequenceSignature: getLoopSignature(loopSequence),
      index,
      startedAtMs: nowMs - offsetInCycle,
    }
  }

  const checkPlanExist = (contentActiveSubscription, userSubscription) => {
    return !isEmpty(intersectionWith(contentActiveSubscription, userSubscription, isEqual))
  }

  const emptySubcheck = async (channelSub) => {
    const subRes = await activeSubCall()
    const activeSub = subRes.data.data
      .filter((item) => item.status)
      .map((item) => item.subscription)

    return !isEmpty(intersectionWith(channelSub, activeSub, isEqual))
  }

  const checkRestriction = async (channelDetail, openModal = false) => {
    if (!channelDetail?.is_restricted) {
      setIsChannelSubscribed(true)
      return true
    }

    if (!isLogin) {
      if (openModal) {
        setModalType('login')
        setOpenLoginModal(true)
      }
      setIsChannelSubscribed(false)
      return false
    }

    const channelSub = [
      ...(channelDetail?.active_subscription || []).map((item) => item._id),
      ...(channelDetail?.active_ppv || []).map((item) => item._id),
    ]

    const planExist = isEmpty(activeSubscription)
      ? await emptySubcheck(channelSub)
      : checkPlanExist(channelSub, activeSubscription)

    if (!planExist) {
      setIsChannelSubscribed(false)
      if (openModal) {
        setRedirectionUrl(window.location.pathname)
        setContentPartners(channelDetail?.owner ? channelDetail.owner : [])
        setSubscriptions(channelSub)
        setOpenSubscriptionModal(true)
      }
      return false
    }

    setIsChannelSubscribed(true)
    return true
  }

  const getPlaylistSignature = (playlist = []) => {
    return playlist
      .map(
        (item) =>
          `${item.asset_id || item.file_name}-${item.start_time}-${item.end_time}-${Boolean(
            item.has_exact_time,
          )}-${Boolean(item.is_looped)}-${item.title || ''}-${item.description || ''}-${
            item.duration || ''
          }`,
      )
      .join('|')
  }

  const pickActiveProgram = () => {
    const playlist = playlistRef.current
    if (isEmpty(playlist) || !isChannelSubscribed) return null

    const nowUtc = moment.utc()
    const nowMs = nowUtc.valueOf()

    // Clear block if the blocked program's end time has passed
    if (blockedProgramRef.current.endMs !== null && nowMs >= blockedProgramRef.current.endMs) {
      blockedProgramRef.current = { startMs: null, endMs: null }
    }

    const latestCompletedExactEndMs = getLatestCompletedExactEndMs(playlist, nowUtc)

    // Retroactively detect recently-completed exact-time programs and block their predecessors
    if (
      latestCompletedExactEndMs > 0 &&
      latestCompletedExactEndMs !== lastSeenExactEndMsRef.current
    ) {
      lastSeenExactEndMsRef.current = latestCompletedExactEndMs

      // Find the exact-time program that just ended
      const justEndedExactProgram = playlist.find(
        (item) => item?.has_exact_time && item?._endMoment?.valueOf() === latestCompletedExactEndMs,
      )

      if (justEndedExactProgram) {
        // Find the normal program that was playing right before it started
        const beforeExactMs = justEndedExactProgram._startMoment?.valueOf()
        const programBeforeExact = playlist
          .filter(
            (item) =>
              !item?.has_exact_time &&
              !item?.is_looped &&
              item?._startMoment?.valueOf() < beforeExactMs &&
              item?._endMoment?.valueOf() > beforeExactMs,
          )
          .pop()

        if (programBeforeExact) {
          blockedProgramRef.current = {
            startMs: programBeforeExact?._startMoment?.valueOf() || null,
            endMs: programBeforeExact?._endMoment?.valueOf() || null,
          }
        }
      }
    }

    const loopSequence = getLoopSequence(
      playlist,
      nowUtc,
      latestCompletedExactEndMs,
      blockedProgramRef.current.startMs,
    )
    const normalProgram = playlist
      .filter(
        (item) =>
          !item?.has_exact_time &&
          !item?.is_looped &&
          !(
            blockedProgramRef.current.startMs !== null &&
            item?._startMoment?.valueOf() <= blockedProgramRef.current.startMs
          ) &&
          nowUtc.isSameOrAfter(item._startMoment) &&
          nowUtc.isBefore(item._endMoment),
      )
      .pop()
    const isInNextDayWindow = nowUtc.isSameOrAfter(moment.utc().startOf('day').add(1, 'day'))

    const exactProgram = playlist.find(
      (item) =>
        item?.has_exact_time &&
        nowUtc.isSameOrAfter(item._startMoment) &&
        nowUtc.isBefore(item._endMoment),
    )
    if (exactProgram) {
      if (currentProgramRef.current?._programKey !== exactProgram._programKey) {
        blockedProgramRef.current = {
          startMs: currentProgramRef.current?._startMoment?.valueOf() || null,
          endMs: currentProgramRef.current?._endMoment?.valueOf() || null,
        }
      }
      loopStateRef.current = { ...loopStateRef.current, active: false, broken: false }
      return exactProgram
    }

    if (currentProgramRef.current?.is_looped) {
      const currentLoopEndMs = currentProgramRef.current?._endMoment?.valueOf() || 0
      if (
        latestCompletedExactEndMs > 0 &&
        currentLoopEndMs > 0 &&
        currentLoopEndMs <= latestCompletedExactEndMs
      ) {
        loopStateRef.current = { ...loopStateRef.current, active: false, broken: true }
      }
    }

    if (!isEmpty(loopSequence)) {
      if (!loopStateRef.current.active) {
        const firstLoopStartMs = loopSequence[0]?._startMoment?.valueOf() || 0
        const shouldResumeLoop =
          currentProgramRef.current?.is_looped || nowUtc.valueOf() >= firstLoopStartMs

        if (shouldResumeLoop) {
          initializeLoopStateFromTimeline(
            loopSequence,
            nowUtc.valueOf(),
            currentProgramRef.current?.is_looped ? currentProgramRef.current?._programKey : null,
          )
        }
      }

      if (loopStateRef.current.active) {
        syncLoopState(loopSequence)
        const activeLoopProgram = getActiveLoopProgram(nowUtc.valueOf())
        if (activeLoopProgram) {
          if (isInNextDayWindow && normalProgram) {
            loopStateRef.current = { ...loopStateRef.current, active: false }
            return normalProgram
          }
          return activeLoopProgram
        }
        loopStateRef.current.active = false
      }

      if (currentProgramRef.current?.is_looped) {
        syncLoopState(loopSequence, currentProgramRef.current?._programKey)
        const activeLoopProgram = getActiveLoopProgram(nowUtc.valueOf())
        if (activeLoopProgram) {
          if (isInNextDayWindow && normalProgram) {
            loopStateRef.current = { ...loopStateRef.current, active: false }
            return normalProgram
          }
          return activeLoopProgram
        }
      }
    }

    if (normalProgram) {
      loopStateRef.current = { ...loopStateRef.current, active: false }
      return normalProgram
    }

    return null
  }

  const switchProgramIfNeeded = (nextProgram) => {
    if (!nextProgram) {
      if (!isEmpty(currentPlayingContent) || videoSource) {
        currentProgramRef.current = null
        setCurrentPlayingContent({})
        setVideoSource(null)
      }
      return
    }

    if (!nextProgram?.playUrl) {
      currentProgramRef.current = null
      setCurrentPlayingContent({})
      setVideoSource(null)
      setVideoURLError('Unable to play this program. Stream URL is missing.')
      return
    }

    const isSameProgram = currentProgramRef.current?._programKey === nextProgram?._programKey
    if (isSameProgram && videoSource === nextProgram.playUrl) {
      if (!isEqual(currentPlayingContent, nextProgram)) {
        currentProgramRef.current = nextProgram
        setCurrentPlayingContent(nextProgram)
      }
      return
    }

    currentProgramRef.current = nextProgram
    setVideoURLError('')
    setCurrentPlayingContent(nextProgram)
    setVideoSource(nextProgram.playUrl)
  }

  const syncPlayback = () => {
    switchProgramIfNeeded(pickActiveProgram())
  }

  const getResumeTime = () => {
    if (isEmpty(currentPlayingContent)) return 0

    const nowMs = moment.utc().valueOf()
    const isCurrentLoopProgram =
      loopStateRef.current?.active &&
      loopStateRef.current?.sequence?.[loopStateRef.current.index]?._programKey ===
        currentPlayingContent?._programKey

    const startMs = isCurrentLoopProgram
      ? loopStateRef.current.startedAtMs
      : currentPlayingContent?._startMoment?.valueOf()

    if (!startMs) return 0

    const elapsedSeconds = Math.floor((nowMs - startMs) / 1000)
    if (elapsedSeconds <= 0) return 0

    const maxDuration =
      Number(currentPlayingContent?.duration) > 0
        ? Math.floor(Number(currentPlayingContent.duration))
        : null

    if (!maxDuration) return elapsedSeconds
    return Math.min(elapsedSeconds, Math.max(0, maxDuration - 1))
  }

  const fetchPlaylist = async () => {
    if (!isChannelSubscribed) return

    try {
      const startTimeUtc = moment().startOf('day').utc().toISOString()
      // Include next-day schedule so looped fallback can hand over as soon as tomorrow's items start.
      const endTimeUtc = moment().startOf('day').add(2, 'day').utc().toISOString()

      const response = await playlistPollingCall({
        params: {
          channel_id: channelId,
          start_time: startTimeUtc,
          end_time: endTimeUtc,
        },
      })
      const result = response?.data

      if (!result?.success || isEmpty(result?.data)) return

      const normalizedPlaylist = normalizePlaylistData(result?.data?.playlist_data || [])

      const nextSignature = getPlaylistSignature(normalizedPlaylist)
      if (playlistSignatureRef.current === nextSignature) return

      playlistSignatureRef.current = nextSignature
      playlistRef.current = normalizedPlaylist
      loopStateRef.current = {
        ...loopStateRef.current,
        broken: false,
      }
      syncPlayback()
    } catch (error) {
      console.error('Playlist fetch failed', error)
    }
  }

  useEffect(() => {
    const syncChannelDetails = async () => {
      if (!channelDetails || !channelDetails.success || isEmpty(channelDetails?.data)) return

      const channelDetail = channelDetails.data[0]
      setDetailObj(channelDetail)

      const isAllowed = await checkRestriction(channelDetail)
      if (!isAllowed) {
        playlistRef.current = []
        playlistSignatureRef.current = ''
        currentProgramRef.current = null
        blockedProgramRef.current = { startMs: null, endMs: null }
        lastSeenExactEndMsRef.current = null
        loopStateRef.current = { ...loopStateRef.current, active: false, broken: false }
        setCurrentPlayingContent({})
        setVideoSource(null)
        return
      }

      await fetchPlaylist()
      syncPlayback()
    }

    syncChannelDetails()
  }, [channelDetails, isLogin, activeSubscription])

  useInterval(() => {
    fetchPlaylist()
  }, 30000)

  useInterval(() => {
    syncPlayback()
  }, 500)

  const getStyleValue = (parentKey, childKey, defaultValue) => {
    if (!data?.style_config) {
      return defaultValue ? defaultValue : ''
    }

    let tempArr = data?.style_config[parentKey]?.data.filter((info) => info.key === childKey)

    return !isEmpty(tempArr) ? (tempArr[0].value ? tempArr[0].value : defaultValue) : defaultValue
  }

  const formatProgramStartTime = (program) => {
    if (!program?.start_time) return '--'

    const startMoment = moment.utc(program.start_time)
    if (!startMoment.isValid()) return '--'

    return startMoment.local().format('hh:mm A')
  }

  const getProgramTilesData = () => {
    const playlist = playlistRef.current || []
    const onAirProgram = !isEmpty(currentPlayingContent) ? currentPlayingContent : null
    const nowUtc = moment.utc()

    const upcomingPrograms = playlist
      .filter((item) => {
        if (!item || item._programKey === onAirProgram?._programKey) return false
        return item?._startMoment?.isAfter(nowUtc)
      })
      .slice(0, 2)

    return {
      onAirProgram,
      upcomingPrograms,
    }
  }

  const { onAirProgram, upcomingPrograms } = getProgramTilesData()

  let componentProps = {
    componentData: {
      id: 'asdasda132132321dasdasda',
      data: { ...data.component_data },
    },
    modifyData: () => {},
    setModalState: () => {},
    isEdit: false,
    onSwap: () => {},
    onDelete: () => {},
    onCopy: () => {},
    onAdd: () => {},
  }

  return (
    <div className="w-full h-full ">
      <div
        className={`${
          iptvLoader && 'animate-pulse'
        } w-full px-2 md:px-[6px] xl:px-[3.66032%] flex flex-col md:flex-row md:justify-between`}
      >
        <div
          style={{ background: '#11171f', aspectRatio: '1.77777' }}
          className="w-full md:w-[48%] xl:w-[46.6%] rounded-md overflow-hidden block md:hidden p-[5px]"
        >
          <div style={{ display: videoSource ? 'block' : 'none', height: '100%' }}>
            <ChannelPlayer
              contentInfo={currentPlayingContent}
              contentWatchUrl={videoSource}
              resumeTime={getResumeTime()}
              watermarkLogo={detailObj?.logo?.website?.s3_key}
              detailObj={detailObj}
              isIptvPlayer={true}
              pageData={data}
            />
          </div>

          {videoURLError && !videoSource ? (
            <div className="w-full h-full flex justify-center items-center">
              <p
                style={{ color: '#cecece' }}
                className="text-xs md:text-base xl:text-lg text-center font-semibold"
              >
                {videoURLError}
              </p>
            </div>
          ) : null}

          {!videoSource ? (
            !isChannelSubscribed ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="flex px-5 md:px-0">
                  <img
                    src={LockIcon}
                    style={{ width: '18.5px', height: '24.6px' }}
                    alt="LockIcon"
                  />
                  <p
                    style={{ color: '#cecece' }}
                    className="md:ml-2 text-xs md:text-base xl:text-lg text-center"
                  >
                    {t('this_channel_not_included')}
                  </p>
                </div>
                <button
                  style={{ background: '#09e9e9', color: '#000' }}
                  className="w-[70%] text-xs md:text-base xl:text-lg rounded-md xl:rounded-lg mt-4 xl:mt-6 py-1"
                  onClick={() => checkRestriction(detailObj, true)}
                >
                  {t('upgrade_membership_to_watch')}
                </button>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <CustomImage
                  imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.logo?.website?.s3_key)}
                  imgAlt="channelimg"
                  imgClass="h-6 w-9 md:h-12 md:w-20"
                  loaderClass="h-6 w-9 md:h-12 md:w-20"
                />
                <p
                  style={{ color: '#cecece' }}
                  className="md:ml-2 text-xs md:text-base xl:text-lg text-center"
                >
                  {t('no_programs_shown_at_moment')}
                </p>
              </div>
            )
          ) : null}
        </div>

        <div
          style={{ background: '#11171f' }}
          className="w-full md:w-[26%] xl:w-[25.27%] h-[279px] md:h-auto rounded-md px-2 md:px-3 xl:px-[12.5px]
          py-[20px] md:py-3  xl:py-[20.1px] mt-2 md:mt-0"
        >
          <div className="w-full flex justify-center items-center  rounded overflow-hidden">
            <CustomImage
              imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.thumbnail?.website?.s3_key)}
              alt="logo"
              imgClass="h-[71.2px] md:h-[53.8px] lg:h-[86px] rounded"
              loaderClass="h-[71.2px] md:h-[53.8px] lg:h-[86px] rounded"
              imgStyle={{ aspectRatio: '1.7777' }}
              loaderStyle={{ aspectRatio: '1.7777' }}
            />
          </div>
          <div
            style={{ color: getStyleValue('channel_profile', 'title_color', '#f8f8f8') }}
            className="text-center w-full line-clamp-2 text-xs md:text-lg 2xl:text-xl font-semibold mt-2.5"
          >
            {detailObj?.name}
          </div>
          <div
            style={{ flexWrap: 'wrap', rowGap: '8px' }}
            className=" w-full flex justify-center items-center mt-[11px] xl:mt-[20px] "
          >
            {!isEmpty(detailObj?.category) &&
              [...detailObj?.category]
                .filter((e, idx) => idx < 2)
                .map((item, index) => {
                  return (
                    <div
                      key={index}
                      style={{
                        color: getStyleValue(
                          'channel_profile',
                          'category_video_text_color',
                          '#ffffff',
                        ),
                        background: getStyleValue(
                          'channel_profile',
                          'category_video_bg_color',
                          '#313945',
                        ),
                        padding: '0px 5.5px',
                        borderRadius: '3px',
                        marginLeft: index === 0 ? '0px' : '9.5px',
                      }}
                      className="flex justify-center items-center line-clamp-1 text-[8px] lg:text-xs 2xl:text-sm w-min"
                    >
                      {item.name}
                    </div>
                  )
                })}

            <div
              style={{
                color: '#ffffff',
                background: '#0f5a22',
                padding: '0px 8.5px',
                borderRadius: '3px',
                marginLeft: '9.5px',
              }}
              className="flex justify-center items-center text-[8px] lg:text-xs 2xl:text-sm w-min"
            >
              FREE-TO-AIR
            </div>

            <div
              style={{
                color: getStyleValue('channel_profile', 'category_video_text_color', '#ffffff'),
                background: getStyleValue('channel_profile', 'category_video_bg_color', '#313945'),
                padding: '0px 5.5px',
                borderRadius: '3px',
                marginLeft: '9.5px',
              }}
              className="flex justify-center items-center line-clamp-1 text-[8px] lg:text-xs 2xl:text-sm w-min"
            >
              HD
            </div>
            <div
              style={{
                color: getStyleValue('channel_profile', 'category_video_text_color', '#ffffff'),
                background: getStyleValue('channel_profile', 'category_video_bg_color', '#313945'),
                padding: '0px 5.5px',
                borderRadius: '3px',
                marginLeft: '9.5px',
              }}
              className="flex justify-center items-center line-clamp-1 text-[8px] lg:text-xs 2xl:text-sm w-min"
            >
              16:9
            </div>
          </div>
          <div
            style={{
              color: getStyleValue('channel_profile', 'description_color', '#acacac'),
              borderTop: '0.5px solid #28303b',
            }}
            className="w-full line-clamp-2 mt-3 pt-3 xl:pt-[1vw] px-3 xl:mt-[1.19326vw] text-[10px] md:text-[8px] lg:text-[11px] 2xl:text-sm"
          >
            {detailObj?.description}
          </div>
          <div className="w-full flex justify-center items-center mt-[28.5px] md:mt-5 px-3 md:px-[6px]">
            <div
              style={{
                background: getStyleValue(
                  'operational_icons_informative_icon',
                  informaticButtonHover === 'favorite' ? 'btn_bg_hover_color' : 'btn_bg_color',
                  informaticButtonHover === 'favorite' ? '363c45' : '#11171f',
                ),
                border: `0.5px solid ${getStyleValue(
                  'operational_icons_informative_icon',
                  'btn_border_color',
                  '#313945',
                )}`,
                color: getStyleValue(
                  'operational_icons_informative_icon',
                  informaticButtonHover === 'favorite'
                    ? 'btn_icons_text_hover_color'
                    : 'btn_icons_text_color',
                  '#ffffff',
                ),
              }}
              className="w-[111px] md:w-[87.7px] lg:w-[121px] h-[27.5px] md:h-[18.8px] lg:h-[30px] flex justify-center items-center text-[10px] cursor-pointer rounded 2xl:text-sm 2xl:h-[35px] 2xl:w-[150px]"
              onMouseEnter={() => setInformaticButtonHover('favorite')}
              onMouseLeave={() => setInformaticButtonHover('')}
            >
              <div
                style={{
                  borderRight: `0.5px solid ${getStyleValue(
                    'operational_icons_informative_icon',
                    'btn_border_color',
                    '#313945',
                  )}`,
                }}
                className="w-[25px] md:w-[16.6px] lg:w-[27.5px] 2xl:w-[32.5px] h-full flex justify-center items-center"
              >
                <IptvFavorite
                  color={getStyleValue(
                    'operational_icons_informative_icon',
                    informaticButtonHover === 'favorite'
                      ? 'btn_icons_text_hover_color'
                      : 'btn_icons_text_color',
                    '#ffffff',
                  )}
                  className="w-[10.6px]  lg:w-[11.6px] 2xl:w-[15.4px] "
                />
              </div>
              <p
                style={{ whiteSpace: 'nowrap' }}
                className="mb-0 flex justify-center items-center w-[86px] md:w-[73.1px] lg:w-[93.5px] 2xl:w-[117.5px]"
              >
                Add to Favorite
              </p>
            </div>
            <div
              style={{
                background: getStyleValue(
                  'operational_icons_informative_icon',
                  informaticButtonHover === 'share' ? 'btn_bg_hover_color' : 'btn_bg_color',
                  informaticButtonHover === 'share' ? '363c45' : '#11171f',
                ),
                border: `0.5px solid ${getStyleValue(
                  'operational_icons_informative_icon',
                  'btn_border_color',
                  '#313945',
                )}`,
                color: getStyleValue(
                  'operational_icons_informative_icon',
                  informaticButtonHover === 'share'
                    ? 'btn_icons_text_hover_color'
                    : 'btn_icons_text_color',
                  '#ffffff',
                ),
              }}
              className="w-[111px] md:w-[87.7px] lg:w-[121px] h-[27.5px] md:h-[18.8px] lg:h-[30px]  flex justify-center items-center text-[10px]  cursor-pointer rounded
              ml-[22.8px] md:ml-[8.6px] lg:ml-[25px] 2xl:text-sm 2xl:h-[35px] 2xl:w-[150px]"
              onMouseEnter={() => setInformaticButtonHover('share')}
              onMouseLeave={() => setInformaticButtonHover('')}
              onClick={() => {
                setShareModal(true)
              }}
            >
              <div
                style={{
                  borderRight: `0.5px solid ${getStyleValue(
                    'operational_icons_informative_icon',
                    'btn_border_color',
                    '#313945',
                  )}`,
                }}
                className="w-[25px] md:w-[16.6px] lg:w-[27.5px] 2xl:w-[32.5px] h-full flex justify-center items-center"
              >
                <IptvShare
                  color={getStyleValue(
                    'operational_icons_informative_icon',
                    informaticButtonHover === 'share'
                      ? 'btn_icons_text_hover_color'
                      : 'btn_icons_text_color',
                    '#ffffff',
                  )}
                  className="w-[10.2px] lg:w-[11.1px] 2xl:w-[15px] "
                />
              </div>
              <p
                style={{ whiteSpace: 'nowrap' }}
                className="mb-0 flex justify-center items-center w-[86px] md:w-[73.1px] lg:w-[93.5px] 2xl:w-[117.5px]"
              >
                Share Channel
              </p>
            </div>
          </div>
        </div>

        <div
          style={{ background: '#11171f', aspectRatio: '1.77777' }}
          className="md:w-[48%] xl:w-[46.6%] rounded-md overflow-hidden hidden md:block p-[5px]"
        >
          {videoSource && (
            <ChannelPlayer
              contentInfo={currentPlayingContent}
              contentWatchUrl={videoSource}
              resumeTime={getResumeTime()}
              watermarkLogo={detailObj?.logo?.website?.s3_key}
              detailObj={detailObj}
              isIptvPlayer={true}
              pageData={data}
            />
          )}

          {videoURLError && !videoSource ? (
            <div className="w-full h-full flex justify-center items-center">
              <p
                style={{ color: '#cecece' }}
                className="text-xs md:text-base xl:text-lg text-center font-semibold"
              >
                {videoURLError}
              </p>
            </div>
          ) : null}

          {!videoSource ? (
            !isChannelSubscribed ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="flex px-5 md:px-0">
                  <img
                    src={LockIcon}
                    style={{ width: '18.5px', height: '24.6px' }}
                    alt="LockIcon"
                  />
                  <p
                    style={{ color: '#cecece' }}
                    className="md:ml-2 text-xs md:text-base xl:text-lg text-center"
                  >
                    {t('this_channel_not_included')}
                  </p>
                </div>
                <button
                  style={{ background: '#09e9e9', color: '#000' }}
                  className="w-[70%] text-xs md:text-base xl:text-lg rounded-md xl:rounded-lg mt-4 xl:mt-6 py-1"
                  onClick={() => checkRestriction(detailObj, true)}
                >
                  {t('upgrade_membership_to_watch')}
                </button>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <CustomImage
                  imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.logo?.website?.s3_key)}
                  imgAlt="channelimg"
                  imgClass="h-6 w-9 md:h-12 md:w-20"
                  loaderClass="h-6 w-9 md:h-12 md:w-20"
                />
                <p
                  style={{ color: '#cecece' }}
                  className="md:ml-2 text-xs md:text-base xl:text-lg text-center"
                >
                  {t('no_programs_shown_at_moment')}
                </p>
              </div>
            )
          ) : null}
        </div>

        <div
          className="h-[246px] md:h-auto w-full md:w-[22.083%] xl:w-[25.27%] flex flex-col justify-between items-center mt-2 md:mt-0
        "
        >
          <div
            style={{
              background: getStyleValue(
                'on_air_program_information_section',
                'section_bg_color',
                '#11171f',
              ),
            }}
            className="w-full h-[36.466%] rounded-md px-[15px] md:px-2 xl:px-[5%] pt-[15px] md:pt-2 xl:pt-[1.244vw]"
          >
            <div className="flex items-center w-full">
              <p
                style={{
                  color: getStyleValue(
                    'on_air_program_information_section',
                    'on_air_text_color',
                    '#fe400f',
                  ),
                }}
                className="mb-0 font-semibold text-[10px] md:text-[8px] lg:text-sm 2xl:text-base w-[32%] xl:w-[28.5%]"
              >
                On Air Now
              </p>
              <p
                style={{
                  color: getStyleValue(
                    'on_air_program_information_section',
                    'start_time_color',
                    '#767779',
                  ),
                }}
                className="mb-0 text-[10px] md:text-[8px] lg:text-xs 2xl:text-sm"
              >
                Start Time: {formatProgramStartTime(onAirProgram)}
              </p>
            </div>
            <p
              style={{
                color: getStyleValue(
                  'on_air_program_information_section',
                  'on_air_program_title_color',
                  '#ffffff',
                ),
                overflowWrap: 'break-word',
              }}
              className="mb-0 font-bold text-[11px] md:text-[10px] line-clamp-3 lg:text-sm 2xl:text-base mt-[6px] xl:mt-[0.6222vw]"
            >
              {onAirProgram?.title || 'No program on air right now.'}
            </p>
          </div>
          <div
            style={{
              background: getStyleValue(
                'up_next_program_information_section',
                'section_bg_color',
                '#11171f',
              ),
            }}
            className="w-full h-[28.733%] rounded-md px-[15px] md:px-2 xl:px-[5%] pt-[10.6px] md:pt-[6.8px] xl:pt-[0.8052vw]"
          >
            <div className="flex items-center w-full">
              <p
                style={{
                  color: getStyleValue(
                    'up_next_program_information_section',
                    'up_next_text_color',
                    '#e5e5e5',
                  ),
                }}
                className="mb-0 font-semibold text-[10px] md:text-[8px] lg:text-sm 2xl:text-base w-[32%]  xl:w-[28.5%] "
              >
                Up Next
              </p>
              <p
                style={{
                  color: getStyleValue(
                    'up_next_program_information_section',
                    'start_time_color',
                    '#767779',
                  ),
                }}
                className="mb-0 text-[10px]  md:text-[8px] lg:text-xs 2xl:text-sm"
              >
                Start Time: {formatProgramStartTime(upcomingPrograms[0])}
              </p>
            </div>
            <p
              style={{
                color: getStyleValue(
                  'up_next_program_information_section',
                  'up_next_program_title_color',
                  '#bebebe',
                ),
                overflowWrap: 'break-word',
              }}
              className="mb-0 text-[11px] line-clamp-2 xl:line-clamp-3 lg:text-xs 2xl:text-sm mt-[6px] xl:mt-[0.6222vw]"
            >
              {upcomingPrograms[0]?.title || 'No upcoming program scheduled.'}
            </p>
          </div>
          <div
            style={{
              background: getStyleValue(
                'up_next_program_information_section',
                'section_bg_color',
                '#11171f',
              ),
            }}
            className="w-full h-[28.733%] rounded-md px-[15px] md:px-2 xl:px-[5%] pt-[10.6px] md:pt-[6.8px] xl:pt-[0.8052vw]"
          >
            <div className="flex items-center w-full">
              <p
                style={{
                  color: getStyleValue(
                    'up_next_program_information_section',
                    'up_next_text_color',
                    '#e5e5e5',
                  ),
                }}
                className="mb-0 font-semibold text-[10px] md:text-[8px] lg:text-sm 2xl:text-base w-[32%]  xl:w-[28.5%] "
              >
                Up Next
              </p>
              <p
                style={{
                  color: getStyleValue(
                    'up_next_program_information_section',
                    'start_time_color',
                    '#767779',
                  ),
                }}
                className="mb-0 text-[10px]  md:text-[8px] lg:text-xs 2xl:text-sm"
              >
                Start Time: {formatProgramStartTime(upcomingPrograms[1])}
              </p>
            </div>
            <p
              style={{
                color: getStyleValue(
                  'up_next_program_information_section',
                  'up_next_program_title_color',
                  '#bebebe',
                ),
                overflowWrap: 'break-word',
              }}
              className="mb-0 text-[11px] line-clamp-2 xl:line-clamp-3 lg:text-xs 2xl:text-sm mt-[6px] xl:mt-[0.6222vw]"
            >
              {upcomingPrograms[1]?.title || 'No upcoming program scheduled.'}
            </p>
          </div>
        </div>
      </div>
      {data.component_enabled ? (
        <div className="w-full">
          <CategoryNavigatorCards {...componentProps} iptvPage={true} />
        </div>
      ) : null}

      <div className="w-full  mx-auto mt-[21px] flex justify-center items-center px-2 md:px-[6px] xl:px-[3.66032%]">
        <IptvPlaylistEPG currentPlayingContent={currentPlayingContent} removeCategories={true} />
      </div>
      <div className="w-full  mx-auto mt-4 px-2 md:px-[6px] xl:px-[3.66032%]">
        <div className="flex items-center gap-2 xl:gap-5 text-[10px] xl:text-xs 2xl:text-sm text-white py-1">
          <div className="flex items-center">
            <div
              className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
              style={{ background: '#11171F', border: '1px solid #727272' }}
            ></div>
            <span className="2xl:ml-3 xl:ml-[10px] ml-1">Program</span>
          </div>
          <div className="flex items-center">
            <div
              className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
              style={{ background: '#141446', border: '1px solid #1F51FF' }}
            ></div>
            <span className="2xl:ml-3 xl:ml-[10px] ml-1">Streaming Now</span>
          </div>
        </div>
      </div>

      {shareModal && (
        <ShareModal
          onClose={() => setShareModal(false)}
          link={create_share_url()}
          detailObj={detailObj}
        />
      )}
    </div>
  )
}

export default withTheme(IptvDefaultPage)
